/*
 * Click, drag, flick. The scroll mechanism lifted from pholoh-rebuild's quick add.
 *
 * Applies to anything marked [data-ml-drag]: category rail, size rails, cart upsell.
 *
 * What makes it feel right, and what the first version got wrong:
 *  - momentum. Release mid-drag and it keeps gliding and decelerates, instead of stopping dead
 *    the instant you let go. This is the whole difference between "smooth" and "terrible";
 *  - it does not grab the pointer until the drag passes 6px, so a plain tap is still a tap and
 *    still selects the size;
 *  - it bows out entirely when the rail has nothing to scroll, so short rails behave like
 *    ordinary content;
 *  - it drives touch as well as mouse, and snapping is off while dragging, so nothing fights
 *    the finger;
 *  - one set of document-level listeners rather than per-element binding, so rails that arrive
 *    later (cart drawer re-render, recommendation rows) work with no rebinding and no
 *    MutationObserver.
 *
 * A drag sets data-ml-dragged on the rail for 60ms. The click handlers on size pills check it
 * so that finishing a drag on top of a pill never adds that size to the bag.
 */
(function () {
	var THRESHOLD = 6;
	var FRICTION = 0.94;
	var MAX_FLICK = 60;

	var rail = null;
	var startX = 0;
	var startScroll = 0;
	var dragging = false;
	var captured = false;
	var pointerId = null;
	var lastX = 0;
	var lastT = 0;
	var velocity = 0; /* px per ms, smoothed */
	var raf = 0;

	var moveRaf = 0;
	var pendingX = 0;

	function stopGlide() {
		if (raf) {
			cancelAnimationFrame(raf);
			raf = 0;
		}
	}

	/* 2026-10-23 (Matej: "udelej swipe carousely plynule, i tazeni mysi"). While a rail comes to rest
	   after a drag, CSS snapping stays off (.is-settling). Taking .is-dragging off at release switched
	   the snap back on at once, the browser jumped to a card, and only then did the smooth settle start:
	   the visible hitch at the end of every drag on the gallery and the upsell. */
	function settleDone(r) {
		clearTimeout(r.__mlSettleTimer);
		r.classList.remove('is-settling');
	}
	function holdSnapOff(r, ms) {
		r.classList.add('is-settling');
		clearTimeout(r.__mlSettleTimer);
		r.__mlSettleTimer = setTimeout(function () { settleDone(r); }, ms);
		if ('onscrollend' in window) {
			r.addEventListener('scrollend', function once() { r.removeEventListener('scrollend', once); setTimeout(function () { settleDone(r); }, 30); });
		}
	}

	/* Land on an item, and let the speed of the flick decide how far.
	 *
	 * A hard flick should carry two or three products, a gentle nudge one, and a drag you stop
	 * dead should settle on whichever is nearest. Free momentum cannot do that: it stops wherever
	 * friction runs out, which on a one-per-view rail is usually halfway between two products. */
	function settleOnItem(r, v, max) {
		var items = Array.prototype.slice.call(r.children);
		if (!items.length) return;

		var base = r.getBoundingClientRect().left - r.scrollLeft;
		var offsets = items.map(function (item) {
			return item.getBoundingClientRect().left - base;
		});

		/* where it would come to rest under friction, then snap that to the nearest item */
		var projected = r.scrollLeft - (v * FRICTION) / (1 - FRICTION);
		projected = Math.max(0, Math.min(max, projected));

		var target = offsets[0];
		var best = Infinity;
		for (var i = 0; i < offsets.length; i++) {
			var d = Math.abs(offsets[i] - projected);
			if (d < best) {
				best = d;
				target = offsets[i];
			}
		}

		r.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
	}

	/* Capture phase throughout. App-injected carousels stop propagation on their own pointer
	   handlers, so a bubble-phase listener on document never hears the drag start at all. */
	document.addEventListener(
		'pointerdown',
		function (event) {
			var target = event.target.closest('[data-ml-drag]');
			if (!target) {
				rail = null;
				return;
			}
			/* Mouse only, deliberately.
			 *
			 * A touch device already scrolls an overflow container natively: off the main thread,
			 * with real momentum and real snapping. Running this script for touch meant calling
			 * preventDefault on every move and reimplementing that in JavaScript on the main
			 * thread, which is strictly worse and is what made the gallery feel laggy and ragged
			 * under a finger. Touch and pen now fall straight through to the browser.
			 *
			 * A mouse cannot drag-scroll natively, which is the only reason this exists. */
			if (event.pointerType !== 'mouse') {
				rail = null;
				return;
			}
			if (event.button !== 0) {
				rail = null;
				return;
			}
			/* Nothing to scroll: leave it completely alone so taps behave normally. */
			if (target.scrollWidth <= target.clientWidth + 1) {
				rail = null;
				return;
			}

			stopGlide(); /* grabbing mid-glide stops it */
			/* A smooth scroll still under way (the upsell autoplay, a settle) keeps animating against
			   the hand and pulled the row back 87px mid-drag. A plain assignment cancels it. */
			target.scrollLeft = target.scrollLeft;
			settleDone(target);

			rail = target;
			dragging = false;
			captured = false;
			pointerId = event.pointerId;
			startX = event.clientX;
			startScroll = target.scrollLeft;
			lastX = event.clientX;
			lastT = event.timeStamp;
			velocity = 0;
		},
		{ passive: true, capture: true }
	);

	document.addEventListener('pointermove', function (event) {
		if (!rail || event.pointerId !== pointerId) return;

		var dx = event.clientX - startX;

		if (!dragging) {
			if (Math.abs(dx) < THRESHOLD) return; /* still a possible tap */
			dragging = true;
			rail.classList.add('is-dragging');
			try {
				rail.setPointerCapture(pointerId);
				captured = true;
			} catch (e) {}
		}

		/* One write per frame: a 500Hz mouse sends several moves per frame and each write is a scroll. */
		pendingX = startScroll - dx;
		if (!moveRaf) {
			var r0 = rail;
			moveRaf = requestAnimationFrame(function () { moveRaf = 0; r0.scrollLeft = pendingX; });
		}

		var dt = event.timeStamp - lastT;
		if (dt > 0) {
			velocity = velocity * 0.7 + ((event.clientX - lastX) / dt) * 0.3;
			lastX = event.clientX;
			lastT = event.timeStamp;
		}
		if (event.cancelable) event.preventDefault();
	}, { capture: true });

	function end(event) {
		if (!rail) return;
		var r = rail;

		if (captured && pointerId != null) {
			try {
				r.releasePointerCapture(pointerId);
			} catch (e) {}
		}
		r.classList.remove('is-dragging');

		if (moveRaf) { cancelAnimationFrame(moveRaf); moveRaf = 0; if (dragging) r.scrollLeft = pendingX; }
		if (dragging) {
			/* Tell the click handlers this was a drag, not a tap. */
			r.dataset.mlDragged = '1';
			setTimeout(function () {
				r.dataset.mlDragged = '';
			}, 60);

			var idle = (event && event.timeStamp ? event.timeStamp : lastT) - lastT;
			var v = (idle < 90 ? velocity : 0) * 16; /* px per frame */
			v = Math.max(-MAX_FLICK, Math.min(MAX_FLICK, v));
			var max = r.scrollWidth - r.clientWidth;

			if (r.dataset.mlDrag === 'snap') {
				/* Carousel mode: one item per view, so it must always come to rest ON an item.
				   Free momentum would leave it parked between two. Instead the flick decides how
				   many items to travel, and the browser animates the last hop. */
				holdSnapOff(r, 900);
				settleOnItem(r, v, max);
			} else if (Math.abs(v) > 1) {
				/* Momentum measured in time, not frames, so it carries as far on a 120Hz screen as on 60Hz. */
				holdSnapOff(r, 2000);
				var last = performance.now();
				(function glide(now) {
					var dtf = Math.min(4, Math.max(0.25, ((now || performance.now()) - last) / 16.67));
					last = now || performance.now();
					r.scrollLeft -= v * dtf;
					v *= Math.pow(FRICTION, dtf);
					if (Math.abs(v) > 0.4 && r.scrollLeft > 0 && r.scrollLeft < max) {
						raf = requestAnimationFrame(glide);
					} else {
						raf = 0;
						settleDone(r);
					}
				})();
			}
		}

		rail = null;
		pointerId = null;
		dragging = false;
		captured = false;
	}

	document.addEventListener('pointerup', end, { capture: true });
	document.addEventListener('pointercancel', end, { capture: true });

	/* Kill native image dragging inside any rail.
	 *
	 * An <img> is draggable by default. Press and move on one and the browser starts its own
	 * drag-and-drop: it lifts a ghost of the picture and stops sending pointer events, so the
	 * carousel stalls mid-gesture. Between two slides there is no image, which is why dragging
	 * there felt fine and dragging the photo did not. The attribute on the tag is the real fix;
	 * this is the guard for any image we did not render ourselves. */
	document.addEventListener(
		'dragstart',
		function (event) {
			if (event.target.closest('[data-ml-drag]')) event.preventDefault();
		},
		true
	);

	/* A drag that ends on a size pill must not add that size. */
	document.addEventListener(
		'click',
		function (event) {
			var inRail = event.target.closest('[data-ml-drag]');
			if (inRail && inRail.dataset.mlDragged === '1') {
				event.stopPropagation();
				event.preventDefault();
			}
		},
		true
	);
})();


/* The community wall opens itself when you reach it. */
(function () {
	function watch() {
		var grid = document.querySelector('[data-ml-ugc-grid]');
		if (!grid || grid.dataset.mlUgcBound) return;
		grid.dataset.mlUgcBound = '1';

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			grid.classList.remove('is-collapsed');
			return;
		}

		new IntersectionObserver(
			function (entries, observer) {
				if (!entries[0].isIntersecting) return;
				grid.classList.remove('is-collapsed');
				observer.disconnect();
			},
			{ threshold: 0.15 }
		).observe(grid);
	}

	if (document.readyState !== 'loading') watch();
	else document.addEventListener('DOMContentLoaded', watch);
})();

/* Adopt any rail we did not build.
 *
 * Apps inject their own upsell carousels into the product column and the cart drawer, with
 * class names that change between versions. Rather than chase those names, look for the shape:
 * a box inside one of our regions that scrolls horizontally and has more content than room.
 * Those get [data-ml-drag] so they click-drag and flick like everything else on the site.
 *
 * Deliberately narrow. Only inside the product info column and the cart drawer, only when the
 * element genuinely overflows, and never the page itself. */
(function () {
	var REGIONS = '.product__info-container, .drawer, cart-drawer';

	function adopt() {
		document.querySelectorAll(REGIONS).forEach(function (region) {
			region.querySelectorAll('*').forEach(function (el) {
				if (el.hasAttribute('data-ml-drag')) return;
				if (el.scrollWidth <= el.clientWidth + 8) return;

				var overflow = getComputedStyle(el).overflowX;
				if (overflow !== 'auto' && overflow !== 'scroll') return;

				el.setAttribute('data-ml-drag', '');
				el.style.cursor = 'grab';
				/* pan-x as well as pan-y. pan-y alone tells the browser the horizontal gesture is
				   not its business, which switches off native horizontal touch scrolling and leaves
				   the rail dead under a finger. */
				el.style.touchAction = 'pan-x pan-y';
				el.style.overscrollBehaviorX = 'contain';
				/* See the note in moonless.css: smooth scrolling and a per-frame scrollLeft do
				   not mix, and the app carousel ships with it on. */
				el.style.scrollBehavior = 'auto';
			});
		});
	}

	var queued;
	function schedule() {
		clearTimeout(queued);
		queued = setTimeout(adopt, 250);
	}

	if (document.readyState !== 'loading') schedule();
	else document.addEventListener('DOMContentLoaded', schedule);

	/* App widgets and the cart drawer both arrive after load. */
	/* Only when elements arrive. The countdowns rewrite their text every second, and walking the
	   drawer with getComputedStyle and scrollWidth on each of those was a long task every second. */
	new MutationObserver(function (list) {
		for (var i = 0; i < list.length; i++) {
			var added = list[i].addedNodes;
			for (var j = 0; j < added.length; j++) { if (added[j].nodeType === 1) { schedule(); return; } }
		}
	}).observe(document.documentElement, {
		childList: true,
		subtree: true,
	});
})();
